/**
 * Fiduciary Render — Computed Variable Producer
 *
 * Takes a validated canonical model and produces a flat dictionary of
 * render variables consumed by both the attorney pre-generation summary
 * and the docxtpl template engine.
 *
 * This module is the SINGLE source of truth for how fiduciary data
 * becomes document text. The attorney summary and the generated document
 * must always agree because they both read from this output.
 */

import { FIDUCIARY_ROLES } from './packageCapabilities.js';
import { getSuccessorCount, hasAnyPair, DEFAULT_MATTER_FACTS } from './fiduciarySchema.js';

// ---------------------------------------------------------------------------
// Firm-Level Defaults
// ---------------------------------------------------------------------------

// Firm-wide defaults for placeholders that identify the supervising paralegal
// on the post-execution To-Do List. These are rendered unless the caller
// supplies a per-matter override via model.firmSettings.
// Update these in one place when staff changes; individual matters can still
// override by populating firm_settings in the import JSON or firmSettings in
// the UI payload.
export const FIRM_DEFAULTS = {
  paralegal_name: 'Emily Johnson',
  paralegal_email: 'ejohnson@tstewartlaw.com',
  paralegal_phone_ext: '704-926-3507',
  // Firm-level identity surfaced in the engagement letter. Per-matter
  // overrides flow through firm_settings (additionalProperties: true on the
  // EP schema permits new keys without a schema bump).
  firm_name: 'Stewart Law, P.A.',
  drafting_attorney: 'Todd A. Stewart',
  drafting_attorney_initials: 'TAS',
  paralegal_initials: 'DL',
};

export const TRUST_INSTRUMENT_TYPES = {
  ORIGINAL: 'original',
  AMENDED_AND_RESTATED: 'amended_and_restated',
};

// Fee estimates per package surfaced in the engagement letter.
// Mirrored to s3_assets/configs/package-pricing.json for documentation;
// this object is the operational source of truth consumed at render time.
// Add other packages as their engagement letters come online.
export const PACKAGE_PRICING = {
  'Disclaimer Package': {
    fee_low: 4000,
    fee_high: 5000,
    fee_range_display: '$4,000 to $5,000',
  },
};

// Reason-prefix that marks a required_confirmations[] entry for inclusion
// in the engagement letter's "Information We Still Need" section. Temporary
// v1.2 convention pending a structured stage field in a future schema bump.
export const ENGAGEMENT_BLOCKING_PREFIX = 'Engagement-blocking:';

// ---------------------------------------------------------------------------
// Co-Fiduciary Action Clause Map (role-specific)
// ---------------------------------------------------------------------------

export const ACTION_CLAUSES = {
  trustee:         { pair: 'The Co-Trustees shall act unanimously.' },
  executor:        { pair: 'The Co-Executors shall act unanimously.' },
  trust_protector: { pair: 'The Co-Trust Protectors shall act unanimously.' },
  guardian:        { pair: 'The Co-Guardians shall act unanimously.' },
  estate_protector: { pair: null },
  financial_agent:  { pair: 'The Co-Agents shall act unanimously.' },
  healthcare_agent: { pair: null },
};

export const TITLE_MAP = {
  trustee:         { single: 'Trustee',         pair: 'Co-Trustees' },
  executor:        { single: 'Executor',        pair: 'Co-Executors' },
  trust_protector: { single: 'Trust Protector', pair: 'Co-Trust Protectors' },
  guardian:        { single: 'Guardian',         pair: 'Co-Guardians' },
  estate_protector: { single: 'Estate Protector', pair: null },
  financial_agent:  { single: 'Agent',           pair: 'Co-Agents' },
  healthcare_agent: { single: 'Healthcare Agent', pair: null },
};

// ---------------------------------------------------------------------------
// Number to Words (legal-style)
// ---------------------------------------------------------------------------

const ONES = [
  '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
  'seventeen', 'eighteen', 'nineteen',
];
const TENS = [
  '', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety',
];

/**
 * Convert an integer (0-99) to spelled-out English words.
 * Used for legal documents: "twenty-five", "thirty", etc.
 * @param {number} n - Integer between 0 and 99
 * @returns {string} Spelled-out number
 */
export function numberToWords(n) {
  if (typeof n !== 'number' || !Number.isInteger(n)) return String(n);
  if (n < 0 || n > 99) return String(n);
  if (n < 20) return ONES[n];
  const ten = TENS[Math.floor(n / 10)];
  const one = ONES[n % 10];
  return one ? `${ten}-${one}` : ten;
}

// ---------------------------------------------------------------------------
// Placeholder Prefix Mapping
// ---------------------------------------------------------------------------

/**
 * Maps role ID + tier index to the placeholder prefix used in templates.
 * This must match the actual placeholder names in the DOCX templates and metadata.
 */
const PLACEHOLDER_PREFIX_MAP = {
  executor: [
    'executor',                      // tier 0: primary
    'successor_executor',            // tier 1: 1st successor
    'second_successor_executor',     // tier 2: 2nd successor
  ],
  trustee: [
    'successor_trustee',             // tier 0: 1st successor (client is implicit initial)
    'second_successor_trustee',      // tier 1: 2nd successor
    'third_successor_trustee',       // tier 2: 3rd successor
  ],
  trust_protector: [
    'first_successor_trust_protector',   // tier 0: 1st successor
    'second_successor_trust_protector',  // tier 1: 2nd successor
    'third_successor_trust_protector',   // tier 2: 3rd successor
  ],
  estate_protector: [
    'estate_protector',              // tier 0: only position
  ],
  guardian: [
    'guardian',                      // tier 0: primary
    'successor_guardian',            // tier 1: successor
  ],
  financial_agent: [
    'agent',                         // tier 0: primary (template uses "agent_name")
    'successor_agent',               // tier 1: successor
  ],
  healthcare_agent: [
    'healthcare_agent',                      // tier 0: primary
    'first_successor_healthcare_agent',      // tier 1: 1st successor
    'second_successor_healthcare_agent',     // tier 2: 2nd successor
  ],
};

// ---------------------------------------------------------------------------
// Name Splitting
// ---------------------------------------------------------------------------

/**
 * Split a full name into first-word/last-word components for casual
 * references in generated documents (e.g., "Husband" column in the To-Do
 * List uses the first word). Middle names are folded into `given_names`.
 *
 * "John Robert Doe" → { first: 'John', given_names: 'John Robert', last: 'Doe' }
 * "Jane Doe"        → { first: 'Jane', given_names: 'Jane',        last: 'Doe' }
 * "Cher"            → { first: 'Cher', given_names: 'Cher',        last: '' }
 */
function splitName(fullName) {
  const name = (fullName || '').trim();
  if (!name) return { first: '', given_names: '', last: '' };
  const parts = name.split(/\s+/);
  if (parts.length === 1) return { first: parts[0], given_names: parts[0], last: '' };
  return {
    first: parts[0],
    given_names: parts.slice(0, -1).join(' '),
    last: parts[parts.length - 1],
  };
}

// ---------------------------------------------------------------------------
// Main Render Function
// ---------------------------------------------------------------------------

/**
 * Compute all render variables from a canonical model.
 *
 * @param {object} model - Validated canonical model
 * @returns {object} Flat key-value dict of all template variables
 */
export function computeRenderVariables(model) {
  const vars = {};

  // --- Client / Spouse / Children ---
  vars.client = model.client?.full_name || '';
  vars.client_residence_county = model.client?.county_of_residence || '';
  vars.spouse = model.spouse?.full_name || '';
  vars.client_date_of_birth = model.client?.date_of_birth || '';

  // Client mailing address — appears in the Certificate of Trust "Currently
  // Acting Trustee" block. Two separate placeholders plus a combined line
  // so templates can either lay out the address in two lines or inline
  // with a comma. The combined line omits the comma cleanly when either
  // part is missing.
  vars.client_street_address = model.client?.street_address || '';
  vars.client_city_state_zip = model.client?.city_state_zip || '';
  vars.client_address_line = [
    vars.client_street_address,
    vars.client_city_state_zip,
  ].filter(Boolean).join(', ');

  // Name splits for casual references (e.g., "Husband" column in To-Do List)
  // and for greetings ("Dear Addie and Bill:"). preferred_first_name takes
  // precedence over the first token of full_name when present; full_name
  // continues to drive given_names / last_name (formal identity, signature
  // blocks). preferred_first_name is the conversational form of the legal
  // first name (Addie ↔ Adelaide) — distinct from a chosen/non-legal name.
  const clientName = splitName(vars.client);
  const clientPreferred = (model.client?.preferred_first_name || '').trim();
  vars.client_first_name = clientPreferred || clientName.first;
  vars.client_given_names = clientName.given_names;
  vars.client_last_name = clientName.last;

  const spouseName = splitName(vars.spouse);
  const spousePreferred = (model.spouse?.preferred_first_name || '').trim();
  vars.spouse_first_name = spousePreferred || spouseName.first;
  vars.spouse_given_names = spouseName.given_names;
  vars.spouse_last_name = spouseName.last;

  const childNames = (model.children || [])
    .map(c => c.full_name)
    .filter(Boolean);
  vars.client_children_names = formatProseList(childNames);

  // --- Dates ---
  vars.signing_date = model.dates?.signing_date || '';
  vars.rlt_date = model.dates?.rlt_date || '';
  // execution_date is an alias used by the post-execution To-Do List
  // template. Kept distinct from signing_date in case the two ever diverge.
  vars.execution_date = vars.signing_date;

  // --- Trust Opening Recitals ---
  // Trust templates consume complete paragraphs here so the same DOCX can
  // generate either an original trust or an amended and restated trust.
  vars.trust_instrument_type = model.packageConfig?.trust_instrument_type === TRUST_INSTRUMENT_TYPES.AMENDED_AND_RESTATED
    ? TRUST_INSTRUMENT_TYPES.AMENDED_AND_RESTATED
    : TRUST_INSTRUMENT_TYPES.ORIGINAL;
  vars.is_amended_and_restated_trust = vars.trust_instrument_type === TRUST_INSTRUMENT_TYPES.AMENDED_AND_RESTATED;
  if (vars.is_amended_and_restated_trust) {
    vars.trust_opening_whereas_clause =
      `WHEREAS, I initially created this trust on ${vars.rlt_date} and I retained the right, by an acknowledged instrument in writing, to revoke or amend it at any time. ` +
      'I now specifically reference my reserved right to amend the Trust and amend the trust agreement by restating it in its entirety as follows and the Trustee is willing to accept the trust hereby created;';
    vars.trust_transfer_clause =
      'NOW, THEREFORE, I hereby transfer the Trust property, including as described on the Schedule attached hereto, to the Trustee, in trust, and the Trustee agrees to accept the property and to hold, manage and distribute it under the terms of this Agreement.';
  } else {
    vars.trust_opening_whereas_clause =
      'WHEREAS, I desire to create a trust and the Trustee is willing to accept the trust hereby created;';
    vars.trust_transfer_clause =
      'NOW, THEREFORE, I hereby transfer the property described on the Schedule attached hereto to the Trustee, in trust, and the Trustee agrees to accept the property and to hold, manage and distribute it under the terms of this Agreement.';
  }

  // --- Firm Settings (paralegal contact for To-Do List header) ---
  const firm = model.firmSettings || {};
  vars.paralegal_name = firm.paralegal_name ?? FIRM_DEFAULTS.paralegal_name;
  vars.paralegal_email = firm.paralegal_email ?? FIRM_DEFAULTS.paralegal_email;
  vars.paralegal_phone_ext = firm.paralegal_phone_ext ?? FIRM_DEFAULTS.paralegal_phone_ext;

  // --- Firm identity for the engagement letter ---
  // Per-matter overrides flow through firm_settings; otherwise the firm-wide
  // FIRM_DEFAULTS values render. engagement_date falls back to signing_date
  // so the letter still dates correctly when the engagement and signing fall
  // on the same day (typical for in-person intake).
  vars.firm_name = firm.firm_name ?? FIRM_DEFAULTS.firm_name;
  vars.drafting_attorney = firm.drafting_attorney ?? FIRM_DEFAULTS.drafting_attorney;
  vars.drafting_attorney_initials = firm.drafting_attorney_initials ?? FIRM_DEFAULTS.drafting_attorney_initials;
  vars.paralegal_initials = firm.paralegal_initials ?? FIRM_DEFAULTS.paralegal_initials;
  vars.engagement_date = firm.engagement_date ?? vars.signing_date ?? '';

  // --- Gateway / conditional variables ---
  vars.has_minor_children = model.packageConfig?.has_minor_children ?? false;
  vars.package_type = model.packageConfig?.package_type || '';

  // --- Matter Facts (drive conditional sections in To-Do List) ---
  // Always emit every flag so templates can rely on the key existing. When a
  // matter doesn't carry facts, render layer falls back to DEFAULT_MATTER_FACTS
  // (all false) so {% if has_x %} blocks are cleanly suppressed.
  const matter = { ...DEFAULT_MATTER_FACTS, ...(model.matterFacts || {}) };
  for (const key of Object.keys(DEFAULT_MATTER_FACTS)) {
    vars[key] = Boolean(matter[key]);
  }

  // Spouse-scoped facts: emitted with `spouse_` prefix. Only populated when
  // the model carries spouseMatterFacts (set on To-Do List opt-in for married
  // packages); otherwise mirrors the primary-client flags so templates can
  // still reference them without a guard.
  const spouseMatter = model.spouseMatterFacts
    ? { ...DEFAULT_MATTER_FACTS, ...model.spouseMatterFacts }
    : matter;
  for (const key of Object.keys(DEFAULT_MATTER_FACTS)) {
    vars[`spouse_${key}`] = Boolean(spouseMatter[key]);
  }
  vars.has_spouse_matter_facts = Boolean(model.spouseMatterFacts);

  // --- Per-role successor counts (replace global successor_levels) ---
  for (const roleId of Object.keys(FIDUCIARY_ROLES)) {
    const count = getSuccessorCount(model, roleId);
    vars[`${roleId}_successor_count`] = count;
  }

  // --- Backward compat: also emit successor_levels as max across roles ---
  const allCounts = Object.keys(FIDUCIARY_ROLES).map(r => getSuccessorCount(model, r));
  vars.successor_levels = Math.min(Math.max(1, Math.max(...allCounts)), 3);

  // --- Per-role/tier render variables ---
  for (const [roleId, assignment] of Object.entries(model.roleAssignments || {})) {
    const prefixes = PLACEHOLDER_PREFIX_MAP[roleId];
    if (!prefixes) continue;

    const tiers = assignment.tiers || [];
    for (let i = 0; i < tiers.length && i < prefixes.length; i++) {
      const prefix = prefixes[i];
      const tier = tiers[i];
      const isPair = tier.people.length === 2;

      // Resolve person data
      const persons = tier.people
        .map(id => model.people[id])
        .filter(Boolean);

      if (persons.length === 0) continue;

      // Name
      vars[`${prefix}_name`] = formatNames(persons);

      // Relationship
      vars[`${prefix}_relationship`] = formatRelationships(persons);

      // Co-fiduciary computed variables
      const titleEntry = TITLE_MAP[roleId];
      const clauseEntry = ACTION_CLAUSES[roleId];

      vars[`${prefix}_title`] = isPair
        ? (titleEntry?.pair || titleEntry?.single || '')
        : (titleEntry?.single || '');

      vars[`${prefix}_acting_clause`] = isPair ? ', acting together' : '';
      vars[`${prefix}_is_pair`] = isPair;
      vars[`${prefix}_action_standard`] = isPair
        ? (clauseEntry?.pair || '')
        : '';

      // Solo-survival clause for paired tiers. Inserted just before the
      // closing period of an appointment paragraph so the surviving
      // co-fiduciary can serve alone without further naming.
      vars[`${prefix}_solo_survival_clause`] = isPair
        ? buildSoloSurvivalClause(persons, titleEntry?.single || '')
        : '';

      // Healthcare agents: contact info from first person
      if (roleId === 'healthcare_agent' && persons[0]) {
        vars[`${prefix}_street_address`] = persons[0].street_address || '';
        vars[`${prefix}_city_and_state_and_zipcode`] = persons[0].city_state_zip || '';
        vars[`${prefix}_telephone`] = persons[0].telephone || '';
      }
    }
  }

  // --- Per-spouse fiduciaries (Option A: joint vs split) ---
  // When spouse_fiduciaries is present in the brief, compute per-tier
  // is_joint flags and spouse_name variables. The existing per-prefix vars
  // above (executor_name, successor_executor_name, ...) carry the client's
  // pick — or the joint pick when both spouses match. Templates use
  // <prefix>_is_joint to decide between a single joint row and side-by-side
  // split rows ("Your X" / "Your spouse's X").
  //
  // Backward compatibility: when spouse_fiduciaries is absent, every tier
  // is_joint defaults to true and spouse_name to '', so existing templates
  // (EL, plan outline, trust) render unchanged.
  //
  // Default-init pass: every known prefix gets <prefix>_is_joint=true and
  // <prefix>_spouse_name='' up front. This prevents the template's
  // {%p if X_is_joint %} block from falling through to the split branch
  // (false/undefined → ELSE) for roles where NEITHER side has an
  // assignment (e.g., a brief that omits trust_protectors entirely). The
  // template additionally gates each row on (name OR spouse_name) so a
  // completely unassigned role drops out instead of rendering an empty
  // row.
  for (const prefixes of Object.values(PLACEHOLDER_PREFIX_MAP)) {
    for (const prefix of prefixes) {
      vars[`${prefix}_is_joint`] = true;
      vars[`${prefix}_spouse_name`] = '';
    }
  }
  const spouseAssignments = model.spouseRoleAssignments || null;
  let anyAsymmetric = false;
  for (const [roleId, prefixes] of Object.entries(PLACEHOLDER_PREFIX_MAP)) {
    const clientTiers = model.roleAssignments?.[roleId]?.tiers || [];
    const spouseTiers = spouseAssignments?.[roleId]?.tiers || [];
    const maxTiers = Math.max(clientTiers.length, spouseTiers.length);

    for (let i = 0; i < maxTiers && i < prefixes.length; i++) {
      const prefix = prefixes[i];
      const clientTier = clientTiers[i];
      const spouseTier = spouseTiers[i];

      // No explicit spouse_fiduciaries → joint with client's pick.
      // Spouse tier missing for this index → spouse defers to client.
      if (!spouseAssignments || !spouseTier || spouseTier.people.length === 0) {
        vars[`${prefix}_is_joint`] = true;
        vars[`${prefix}_spouse_name`] = '';
        continue;
      }

      const clientIds = clientTier ? [...clientTier.people].sort().join('|') : '';
      const spouseIds = [...spouseTier.people].sort().join('|');
      const tierIsJoint = clientIds === spouseIds && clientIds !== '';
      vars[`${prefix}_is_joint`] = tierIsJoint;

      if (tierIsJoint) {
        vars[`${prefix}_spouse_name`] = '';
      } else {
        anyAsymmetric = true;
        const spousePersons = spouseTier.people
          .map(id => model.people[id])
          .filter(Boolean);
        vars[`${prefix}_spouse_name`] = spousePersons.length > 0
          ? formatNames(spousePersons)
          : '';

        // Healthcare-agent contact info: emit a parallel set for the
        // spouse's pick so the Planning Confirmation can render two
        // complete contact blocks (name + street + city + phone).
        if (roleId === 'healthcare_agent' && spousePersons[0]) {
          vars[`${prefix}_spouse_street_address`] = spousePersons[0].street_address || '';
          vars[`${prefix}_spouse_city_and_state_and_zipcode`] = spousePersons[0].city_state_zip || '';
          vars[`${prefix}_spouse_telephone`] = spousePersons[0].telephone || '';
        }
      }
    }
  }
  vars.has_asymmetric_picks = anyAsymmetric;
  vars.is_married = !!(model.spouse && model.spouse.full_name);

  // --- Has any co-trustee (for multiple trustees clause) ---
  vars.has_any_co_trustee = hasAnyPair(model, 'trustee');

  // --- Age thresholds (numeric + spelled out) ---
  const tc = model.trustConfig || {};
  const ageFields = [
    ['beneficiary_cotrust_age', tc.beneficiary_cotrust_age],
    ['beneficiary_sole_trust_age', tc.beneficiary_sole_trust_age],
    ['trust_protector_age', tc.trust_protector_age],
    ['pot_trust_termination_age', tc.pot_trust_termination_age],
  ];
  for (const [key, value] of ageFields) {
    if (value != null) {
      vars[key] = value;
      vars[`${key}_text`] = numberToWords(value);
    }
  }

  // --- Specific Gifts ---
  // All specific gifts live in the Trust (pour-over Will design).
  // `kind` selects the Trust article where each gift renders:
  //   'tpp'           → Trust Article V subsection (Specific Gift of TPP)
  //   'pre_residuary' → new Trust article (Pre-Residuary Gifts), placed after
  //                     Article V and before the residuary/formula articles
  // Emit a combined loopable list plus per-kind subsets for template loops.
  const gifts = Array.isArray(model.specific_gifts) ? model.specific_gifts : [];
  const renderedGifts = gifts
    .map(g => {
      const person = model.people[g.recipient_id];
      if (!person) return null;
      const kind = g.kind === 'tpp' ? 'tpp' : 'pre_residuary';
      return {
        recipient_name: person.name,
        recipient_relationship: person.relationship || '',
        description: g.description,
        offset_against_residuary: !!g.offset_against_residuary,
        in_trust: kind === 'pre_residuary' && !!g.in_trust,
        kind,
      };
    })
    .filter(Boolean);

  vars.specific_gifts = renderedGifts;
  vars.tangible_gifts = renderedGifts.filter(g => g.kind === 'tpp');
  vars.pre_residuary_gifts = renderedGifts.filter(g => g.kind === 'pre_residuary');
  vars.has_specific_gifts = renderedGifts.length > 0;
  vars.has_tangible_gifts = vars.tangible_gifts.length > 0;
  vars.has_pre_residuary_gifts = vars.pre_residuary_gifts.length > 0;
  vars.has_any_residuary_offset = renderedGifts.some(g => g.offset_against_residuary);

  // --- Engagement letter: fee estimate ---
  const pricing = PACKAGE_PRICING[vars.package_type];
  vars.fee_low = pricing?.fee_low ?? '';
  vars.fee_high = pricing?.fee_high ?? '';
  vars.fee_range_display = pricing?.fee_range_display ?? '';

  // --- Planning Confirmation: client-knowable open items ---
  // Routes required_confirmations[] entries tagged with home === 'planning_confirmation'
  // into the Planning Confirmation's "Information We Still Need Before Drafting"
  // section. Legacy 'Engagement-blocking:' reason-prefix is accepted as an
  // alias for one release. ambiguities[] is not pulled — those are drafting
  // questions for attorney review, not client-facing confirmations.
  const confirmations = Array.isArray(model.required_confirmations)
    ? model.required_confirmations
    : [];
  vars.planning_confirmation_items = confirmations
    .map(c => {
      if (!c) return null;
      if (c.home === 'planning_confirmation') {
        return (c.text || c.reason || '').trim();
      }
      if (typeof c.reason === 'string' && c.reason.startsWith(ENGAGEMENT_BLOCKING_PREFIX)) {
        return c.reason.slice(ENGAGEMENT_BLOCKING_PREFIX.length).trim();
      }
      return null;
    })
    .filter(Boolean);

  // Legacy alias: the old EL template still references {{open_items}} via a
  // `{% if open_items %}` block. Emit an empty list so that block is suppressed
  // cleanly until the EL template removes the section. Drop this once the EL
  // template no longer references open_items.
  vars.open_items = [];

  // --- Planning Confirmation: assumptions + timeline ---
  // plan_assumptions_text is authored upstream by Automation (plain-English
  // restatement of the plan). engagement_timeline_text has a SED default so
  // the section is never empty.
  vars.plan_assumptions_text = model.planning_confirmation?.plan_assumptions_text || '';
  vars.engagement_timeline_text =
    model.planning_confirmation?.engagement_timeline_text
    || 'Drafting will begin once the engagement letter is signed, the deposit is paid, and the items above are confirmed.';

  // --- Survivorship ---
  const direction = model.packageConfig?.survivorship_direction || 'spouse_survives';
  vars.survivorship_direction = direction;
  if (direction === 'spouse_survives') {
    vars.survivorship_clause =
      'my Spouse shall be deemed to have survived me for purposes of this Article. ' +
      'Further, my Spouse shall be deemed to have survived me if my Spouse actually survives me for any period of time, however short.';
  } else {
    vars.survivorship_clause =
      'I shall be deemed to have survived my Spouse for purposes of this Article. ' +
      'Further, I shall be deemed to have survived my Spouse if I actually survive my Spouse for any period of time, however short.';
  }

  return vars;
}

// ---------------------------------------------------------------------------
// Name & Relationship Formatting
// ---------------------------------------------------------------------------

/**
 * Format a list for prose in generated legal documents.
 * Single: "A"
 * Pair:   "A and B"
 * Three+: "A, B, and C"
 */
export function formatProseList(items) {
  const clean = (items || [])
    .map(item => String(item || '').trim())
    .filter(Boolean);

  if (clean.length === 0) return '';
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  return `${clean.slice(0, -1).join(', ')}, and ${clean[clean.length - 1]}`;
}

/**
 * Format one or two person names for document text.
 * Single: "Jane Marie Doe"
 * Pair:   "Fred Doe and Jenny Doe"
 */
function formatNames(persons) {
  return formatProseList(persons.map(p => p.name));
}

/**
 * Format one or two person relationships for document text.
 * Single: "wife"
 * Pair:   "brother and my sister"
 *
 * Templates already have a literal "my " (or "My ") before the relationship
 * placeholder, so we emit the bare relationship word. For pairs we prefix
 * the second person with "my " so the rendered text reads "my X and my Y".
 */
function formatRelationships(persons) {
  const bare = persons
    .map(p => {
      const rel = (p.relationship || '').trim();
      if (!rel) return '';
      return rel.toLowerCase().startsWith('my ') ? rel.slice(3).trim() : rel;
    })
    .filter(Boolean);

  if (bare.length === 0) return '';
  if (bare.length === 1) return bare[0];
  if (bare.length === 2) return `${bare[0]} and my ${bare[1]}`;
  return bare.join(', ');
}

/**
 * Build the solo-survival clause for a paired-tier appointment paragraph.
 * Returns empty string when not applicable so templates that always
 * include {{...solo_survival_clause}} render cleanly for solo tiers.
 *
 * Example: "; provided, however, that if either my daughter or my son
 * fails to serve as Trustee, the other may serve alone"
 *
 * Intentionally omits trailing punctuation — the template's existing
 * sentence-ending period sits immediately after this placeholder.
 */
function buildSoloSurvivalClause(persons, roleSingular) {
  if (!Array.isArray(persons) || persons.length !== 2) return '';
  if (!roleSingular) return '';
  const bareRels = persons
    .map(p => (p?.relationship || '').trim())
    .map(rel => (rel.toLowerCase().startsWith('my ') ? rel.slice(3).trim() : rel))
    .filter(Boolean);
  if (bareRels.length !== 2) return '';
  return `; provided, however, that if either my ${bareRels[0]} or my ${bareRels[1]} fails to serve as ${roleSingular}, the other may serve alone`;
}

// ---------------------------------------------------------------------------
// Generation Manifest
// ---------------------------------------------------------------------------

/**
 * Build a generation manifest from the canonical model and render variables.
 * This is the deterministic audit artifact saved alongside generated documents.
 *
 * @param {object} model - Canonical model
 * @param {object} renderVars - Output of computeRenderVariables()
 * @returns {object} Manifest object
 */
export function buildGenerationManifest(model, renderVars) {
  const roleSummary = {};
  for (const [roleId, assignment] of Object.entries(model.roleAssignments || {})) {
    const tiers = (assignment.tiers || []).map((tier, idx) => {
      const persons = tier.people.map(id => model.people[id]).filter(Boolean);
      return {
        tier: idx,
        people: persons.map(p => ({ name: p.name, relationship: p.relationship })),
        is_pair: persons.length === 2,
      };
    });
    if (tiers.length > 0) {
      roleSummary[roleId] = {
        successor_count: getSuccessorCount(model, roleId),
        tiers,
      };
    }
  }

  return {
    generated_at: new Date().toISOString(),
    package_type: model.packageConfig?.package_type,
    client: model.client?.full_name,
    spouse: model.spouse?.full_name || null,
    children: (model.children || []).map(c => ({
      name: c.full_name,
      is_minor: c.is_minor,
    })),
    has_minor_children: model.packageConfig?.has_minor_children,
    survivorship_direction: model.packageConfig?.survivorship_direction,
    trust_instrument_type: model.packageConfig?.trust_instrument_type || TRUST_INSTRUMENT_TYPES.ORIGINAL,
    trust_config: { ...model.trustConfig },
    matter_facts: model.matterFacts ? { ...model.matterFacts } : null,
    spouse_matter_facts: model.spouseMatterFacts ? { ...model.spouseMatterFacts } : null,
    firm_settings: model.firmSettings ? { ...model.firmSettings } : null,
    fiduciary_appointments: roleSummary,
    render_variables: renderVars,
  };
}
